import React, { useEffect, useState, useRef } from 'react';
import SideNavbar from '../../components/SideNavbar';
import TopNavbar from '../../components/TopNavbar';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp, addDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useStudentAuth } from '../../StudentAuthContext';
import Toast from '../../components/Toast';
import { useAppSelector } from '../../redux/store';

interface Ticket {
    id: string;
    requestedById: string;
    subject: string;
    status: 'Open' | 'In Progress' | 'Resolved' | 'Closed';
    priority: 'High' | 'Medium' | 'Low';
    createdDate: Timestamp;
    dueDate: Timestamp;
    isActive: boolean;
}

interface NewTicketModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (subject: string, priority: 'High' | 'Medium' | 'Low') => void;
}

const NewTicketModal: React.FC<NewTicketModalProps> = ({ isOpen, onClose, onSubmit }) => {
    const [subject, setSubject] = useState('');
    const [priority, setPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (subject.trim()) {
            onSubmit(subject, priority);
            setSubject('');
            setPriority('Medium');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-montserrat text-gray-900">Create New Ticket</h2>
                    <button onClick={onClose}>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/desktop/close.svg`}
                            alt="Close"
                            className="w-6 h-6"
                        />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block font-montserrat text-sm text-gray-500 mb-2">Subject</label>
                        <textarea
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={4}
                            placeholder="Enter ticket subject..."
                        />
                    </div>

                    <div>
                        <label className="block font-montserrat text-sm text-gray-500 mb-2">Priority</label>
                        <select
                            value={priority}
                            onChange={(e) => setPriority(e.target.value as 'High' | 'Medium' | 'Low')}
                            className="w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                        </select>
                    </div>

                    <div className="flex justify-end space-x-4 mt-8">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2 text-white rounded-full"
                            style={{ background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)' }}
                            disabled={!subject.trim()}
                        >
                            Create Ticket
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const StudentRaiseTicket = () => {
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [statusCounts, setStatusCounts] = useState({
        Open: 0,
        'In Progress': 0,
        Resolved: 0,
        Closed: 0
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filters, setFilters] = useState({
        status: [] as string[],
        priority: [] as string[]
    });
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [isNewTicketModalOpen, setIsNewTicketModalOpen] = useState(false);
    const filterPopupRef = useRef<HTMLDivElement | null>(null);
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);

    const { currentStudent } = useStudentAuth();
    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);

    useEffect(() => {
        if (currentStudent?.companyId && currentStudent?.leadUid) {
            fetchTickets();
        }
    }, [currentStudent]);

    const fetchTickets = async () => {
        if (!currentStudent?.companyId || !currentStudent?.leadUid) return;

        try {
            // Calculate date 6 months ago
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

            const ticketsQuery = query(
                collection(db, `companies/${currentStudent.companyId}/tickets`),
                where('requestedById', '==', currentStudent.leadUid),
                where('isActive', '==', true),
                where('createdDate', '>=', Timestamp.fromDate(sixMonthsAgo))
            );

            const ticketsSnapshot = await getDocs(ticketsQuery);
            const ticketsData: Ticket[] = [];
            const counts = { Open: 0, 'In Progress': 0, Resolved: 0, Closed: 0 };

            for (const ticketDoc of ticketsSnapshot.docs) {
                const ticketData = ticketDoc.data();
                const ticket = {
                    ...ticketData,
                    id: ticketDoc.id
                } as Ticket;

                ticketsData.push(ticket);
                counts[ticket.status]++;
            }

            setTickets(ticketsData);
            setStatusCounts(counts);
        } catch (error) {
            console.error('Error fetching tickets:', error);
        }
    };

    const handleFilterChange = (filterType: keyof typeof filters, value: string) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: prev[filterType].includes(value)
                ? prev[filterType].filter(item => item !== value)
                : [...prev[filterType], value]
        }));
    };

    const clearFilters = () => {
        setFilters({
            status: [],
            priority: []
        });
        setIsFilterOpen(false);
    };

    const applyFilters = () => {
        setIsFilterOpen(false);
    };

    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch = ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ticket.id.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesStatus = filters.status.length === 0 || filters.status.includes(ticket.status);
        const matchesPriority = filters.priority.length === 0 || filters.priority.includes(ticket.priority);

        return matchesSearch && matchesStatus && matchesPriority;
    });

    const statusBoxes = [
        { status: 'Open', count: statusCounts.Open, color: 'border-blue-500', icon: 'tabler-progress-alert.svg' },
        { status: 'In Progress', count: statusCounts['In Progress'], color: 'border-yellow-500', icon: 'tabler_progress-check.svg' },
        { status: 'Resolved', count: statusCounts.Resolved, color: 'border-green-500', icon: 'teenyicons_folder-tick-outline.svg' },
        { status: 'Closed', count: statusCounts.Closed, color: 'border-red-500', icon: 'material-symbols_tab-close-outline.svg' }
    ];

    const TicketDetailModal = ({ ticket, onClose }: { ticket: Ticket | null; onClose: () => void }) => {
        if (!ticket) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-montserrat text-gray-900">Ticket Details</h2>
                        <button onClick={onClose}>
                            <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/close.svg`}
                                alt="Close"
                                className="w-6 h-6"
                            />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <h3 className="font-montserrat text-sm text-gray-500 mb-1">Ticket ID</h3>
                            <p className="font-montserrat text-gray-900">{ticket.id.slice(0, 8)}</p>
                        </div>
                        <div>
                            <h3 className="font-montserrat text-sm text-gray-500 mb-1">Status</h3>
                            <span className={`px-2 py-1 rounded-full ${ticket.status === 'Open' ? 'bg-blue-100 text-blue-800' :
                                    ticket.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                                        ticket.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                                            'bg-red-100 text-red-800'
                                }`}>
                                {ticket.status}
                            </span>
                        </div>
                        <div>
                            <h3 className="font-montserrat text-sm text-gray-500 mb-1">Created On</h3>
                            <p className="font-montserrat text-gray-900">
                                {ticket.createdDate.toDate().toLocaleDateString()}
                            </p>
                        </div>
                        <div>
                            <h3 className="font-montserrat text-sm text-gray-500 mb-1">Due Date</h3>
                            <p className="font-montserrat text-gray-900">
                                {ticket.dueDate.toDate().toLocaleDateString()}
                            </p>
                        </div>
                        <div>
                            <h3 className="font-montserrat text-sm text-gray-500 mb-1">Priority</h3>
                            <span className={`px-2 py-1 rounded-full ${ticket.priority === 'High' ? 'bg-red-100 text-red-800' :
                                    ticket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-green-100 text-green-800'
                                }`}>
                                {ticket.priority}
                            </span>
                        </div>
                        <div className="col-span-2">
                            <h3 className="font-montserrat text-sm text-gray-500 mb-1">Subject</h3>
                            <p className="font-montserrat text-gray-900 whitespace-pre-wrap">{ticket.subject}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const handleCreateTicket = async (subject: string, priority: 'High' | 'Medium' | 'Low') => {
        if (!currentStudent?.companyId || !currentStudent?.leadUid) {
            setToastMessage('Unable to create ticket. User information is missing.');
            setIsErrorToast(true);
            setShowToast(true);
            return;
        }
    
        try {
            const leadDoc = await getDoc(doc(db, `companies/${currentStudent.companyId}/leads/${currentStudent.leadUid}`));
            const leadData = leadDoc.data();
            if (!leadData?.assignedTo) {
                setToastMessage('Error: No assigned support person found.');
                setIsErrorToast(true);
                setShowToast(true);
                return;
            }
    
            const now = new Date();
            const dueDate = new Date();
            dueDate.setDate(now.getDate() + 7); 
    
            const ticketData = {
                subject,
                status: 'Open',
                priority,
                requestedById: currentStudent.leadUid,
                assigneeId: leadData.assignedTo,
                createdDate: Timestamp.fromDate(now),
                dueDate: Timestamp.fromDate(dueDate),
                isActive: true
            };
    
            await addDoc(collection(db, `companies/${currentStudent.companyId}/tickets`), ticketData);
            setIsNewTicketModalOpen(false);
            fetchTickets();
            
            setToastMessage('Ticket created successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error('Error creating ticket:', error);
            setToastMessage('Failed to create ticket. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
        }
    };

    return (
        <div className="min-h-screen bg-gray-200">
            {/* <div className="fixed top-0 left-0 w-full z-10">
                <TopNavbar />
            </div> */}
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-6 rounded-3xl shadow-md w-full">
                    <div className="flex items-center gap-6 mb-6">
                        <div
                            className="flex items-center justify-center w-14 h-14 rounded-full"
                            style={{ background: 'linear-gradient(180deg, #00A6D9 10%, #3483BE 100%)' }}
                        >
                            <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/tickets.svg`}
                                alt="Ticket Icon"
                                className="w-8 h-8"
                            />
                        </div>
                        <h2 className="font-montserrat text-2xl font-medium text-black">My Tickets</h2>
                    </div>
                    <div>
                        <button
                            className="mt-2 mb-6 text-white py-2 px-4 rounded-full"
                            style={{ background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)' }}
                            onClick={() => setIsNewTicketModalOpen(true)}
                        >
                            Raise A New Ticket
                        </button>
                    </div>
                    <div className="flex gap-12 mb-8 w-2/3">
                        {statusBoxes.map((item, index) => (
                            <div
                                key={index}
                                className="flex-1 bg-gray-100 rounded-3xl p-2 shadow-md h-20"
                                style={{
                                    borderBottom: `4px solid transparent`,
                                    borderImage: `linear-gradient(to right, transparent 20%,  ${item.color} 50%, transparent 80%) 1`
                                }}
                            >
                                <div className="flex flex-col h-full">
                                    <div className="flex justify-center mb-1">
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/${item.icon}`}
                                            alt={`${item.status} Icon`}
                                            className="w-6 h-6"
                                        />
                                    </div>
                                    <div className="flex justify-between items-end mt-auto">
                                        <span className="text-lg font-Montserrat-medium text-gray-900">{item.count}</span>
                                        <span className="text-xs font-Montserrat-medium text-gray-600">{item.status}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <h3 className="font-montserrat text-lg font-medium text-black mb-4">Manage Tickets</h3>
                    <div className='w-full flex items-center justify-between mb-6'>
                        <div className="flex items-center rounded-3xl bg-zinc-100 w-80">
                            <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`}
                                alt="Search Icon"
                                className="w-5 h-5 ml-4"
                            />
                            <input
                                type="text"
                                placeholder="Search tickets..."
                                className="px-4 py-2 text-gray-700 bg-zinc-100 focus:outline-none rounded-3xl w-full"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div className="relative">
                            <button
                                onClick={() => setIsFilterOpen(!isFilterOpen)}
                                className="text-gray-600 hover:text-gray-800"
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/NavFilter.svg`}
                                    alt="filters-icon"
                                    className="w-[35px] h-[35px]"
                                />
                            </button>

                            {isFilterOpen && (
                                <div
                                    ref={filterPopupRef}
                                    className="absolute top-0 right-0 transform translate-x-3 w-[261px] bg-white border rounded-3xl shadow-lg p-4 z-50"
                                >
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-[14px] font-normal leading-[20px] text-left">Filters</h3>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/black-filter.svg`}
                                            alt="filters-black-icon"
                                            onClick={() => setIsFilterOpen(false)}
                                            className="w-9 h-9 cursor-pointer"
                                        />
                                    </div>

                                    <div className="mt-4 space-y-4">
                                        <div>
                                            <span className="text-[12px] font-normal leading-[16px]">Status:</span>
                                            <div className="mt-2 space-y-1">
                                                {['Open', 'In Progress', 'Resolved', 'Closed'].map(status => (
                                                    <label key={status} className="flex items-center space-x-2 cursor-pointer">
                                                        <div className="relative">
                                                            <input
                                                                type="checkbox"
                                                                checked={filters.status.includes(status)}
                                                                onChange={() => handleFilterChange('status', status)}
                                                                className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors"
                                                            />
                                                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none z-10">
                                                                <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                                                            </div>
                                                        </div>
                                                        <span className="text-[12px]">{status}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <span className="text-[12px] font-normal leading-[16px]">Priority:</span>
                                            <div className="mt-2 space-y-1">
                                                {['High', 'Medium', 'Low'].map(priority => (
                                                    <label key={priority} className="flex items-center space-x-2 cursor-pointer">
                                                        <div className="relative">
                                                            <input
                                                                type="checkbox"
                                                                checked={filters.priority.includes(priority)}
                                                                onChange={() => handleFilterChange('priority', priority)}
                                                                className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors"
                                                            />
                                                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none z-10">
                                                                <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                                                            </div>
                                                        </div>
                                                        <span className="text-[12px]">{priority}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-between space-x-3 mt-4">
                                        <button
                                            onClick={clearFilters}
                                            className="px-8 py-2 text-white bg-black rounded-full text-sm"
                                        >
                                            Clear
                                        </button>
                                        <button
                                            onClick={applyFilters}
                                            className="px-8 py-2 text-white bg-cyan-600 rounded-full text-sm"
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="rounded-3xl overflow-hidden shadow-lg">
                        <div className="overflow-x-auto relative">
                            <table className="min-w-full table-auto bg-zinc-100">
                                <thead>
                                    <tr className="text-center bg-[#02385E] text-white">
                                        <th className="font-base font-normal p-2 text-zinc-100 font-Montserrat w-[100px]">ID</th>
                                        <th className="font-base font-normal p-2 text-zinc-100 font-Montserrat w-[140px]">Status</th>
                                        <th className="font-base font-normal p-2 text-zinc-100 font-Montserrat w-[120px]">Priority</th>
                                        <th className="font-base font-normal p-2 text-zinc-100 font-Montserrat w-[120px]">Created Date</th>
                                        <th className="font-base font-normal p-2 text-zinc-100 font-Montserrat w-[120px]">Due Date</th>
                                        <th className="font-base font-normal p-2 text-zinc-100 font-Montserrat w-[120px]">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTickets.map((ticket) => (
                                        <tr key={ticket.id} className="border-b">
                                            <td className="font-base font-normal p-2 text-neutral-600 text-center font-Montserrat w-[100px]">
                                                <div className="truncate" title={ticket.id}>{ticket.id.slice(0, 8)}</div>
                                            </td>
                                            <td className="font-base font-normal p-2 text-neutral-600 text-center font-Montserrat w-[140px]">
                                                <span className={`inline-block min-w-[100px] px-2 py-1 rounded-full ${ticket.status === 'Open' ? 'bg-blue-100 text-blue-800' :
                                                        ticket.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                                                            ticket.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                                                                'bg-red-100 text-red-800'
                                                    }`}>
                                                    {ticket.status}
                                                </span>
                                            </td>
                                            <td className="font-base font-normal p-2 text-neutral-600 text-center font-Montserrat w-[120px]">
                                                <span className={`inline-block min-w-[80px] px-2 py-1 rounded-full ${ticket.priority === 'High' ? 'bg-red-100 text-red-800' :
                                                        ticket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                                            'bg-green-100 text-green-800'
                                                    }`}>
                                                    {ticket.priority}
                                                </span>
                                            </td>
                                            <td className="font-base font-normal p-2 text-neutral-600 text-center font-Montserrat w-[120px]">
                                                <div className="truncate" title={ticket.createdDate.toDate().toLocaleDateString()}>
                                                    {ticket.createdDate.toDate().toLocaleDateString()}
                                                </div>
                                            </td>
                                            <td className="font-base font-normal p-2 text-neutral-600 text-center font-Montserrat w-[120px]">
                                                <div className="truncate" title={ticket.dueDate.toDate().toLocaleDateString()}>
                                                    {ticket.dueDate.toDate().toLocaleDateString()}
                                                </div>
                                            </td>
                                            <td className="font-base font-normal p-2 text-neutral-600 text-center font-Montserrat w-[120px]">
                                                <div className="flex justify-center">
                                                    <img
                                                        src={`${process.env.PUBLIC_URL}/images/desktop/lucide-view.svg`}
                                                        alt="View Details"
                                                        className="w-5 h-5 cursor-pointer"
                                                        onClick={() => setSelectedTicket(ticket)}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            {selectedTicket && (
                <TicketDetailModal
                    ticket={selectedTicket}
                    onClose={() => setSelectedTicket(null)}
                />
            )}
            <NewTicketModal
                isOpen={isNewTicketModalOpen}
                onClose={() => setIsNewTicketModalOpen(false)}
                onSubmit={handleCreateTicket}
            />
            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};

export default StudentRaiseTicket;
